// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { Program } from 'models';

// ui
import { UitkIcon } from 'uitk-react-icons';
import { UitkText } from 'uitk-react-text';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';

export interface ProgramCardUserProps {
  l10n: ILocalization;
  partnerId: string;
  camref?: string;
  partnerSignedUp: boolean;
  program: Program;
  programStatus: string;
  programStatusVerified?: boolean;
}

export const ProgramCardUser: React.FunctionComponent<ProgramCardUserProps> = ({
  l10n,
  partnerId,
  partnerSignedUp,
  program,
  camref,
  programStatus,
  programStatusVerified,
}) => {
  if (!program.live) {
    return null;
  }

  const getsStatusDetails = () => {
    let statusIcon = null;
    let statusMessage = null;
    let statusTheme = null;

    const statusArray = ['PENDING', 'DECLINED', 'EXPIRED'];
    if (programStatus && statusArray.includes(programStatus)) {
      statusMessage = l10n.formatText(`program.${programStatus.toString().toLowerCase()}.message`);
    }

    const statusIconMap = new Map([
      ['APPROVED', 'done'],
      ['PENDING', 'info'],
      ['EXPIRED', 'warning'],
      ['DECLINED', 'cancel'],
    ]);

    const statusThemeMap = new Map([
      ['APPROVED', 'positive'],
      ['PENDING', null],
      ['EXPIRED', 'negative'],
      ['DECLINED', 'negative'],
    ]);

    /* istanbul ignore else */
    if (programStatus) {
      if (statusIconMap.has(programStatus)) {
        statusIcon = statusIconMap.get(programStatus);
      } else {
        statusIcon = 'info';
      }

      if (statusThemeMap.has(programStatus)) {
        statusTheme = statusThemeMap.get(programStatus);
      } else {
        statusTheme = 'info';
      }
    }

    return {
      icon: statusIcon,
      message: statusMessage,
      theme: statusTheme,
    };
  };

  if (!partnerSignedUp && !programStatusVerified) {
    if(program.network === 'CJ') {
      return (
        <UitkText align="center">
          <LocalizedText message="partner.sign.up.message" />{' '}
          <UitkLink inline={true}>
            <Link to={{ search: `pwaDialog=verifyPartner-${program.name}` }}>
              <button>
                <LocalizedText message="partner.sign.up.link" />
              </button>
            </Link>
          </UitkLink>
        </UitkText>
      );
    } else {
      return (<></>);
    }
  }

  if (programStatusVerified) {
    return (
      <UitkText align="center">
        <LocalizedText message="verify.program.status.success" />{' '}
      </UitkText>
    );
  }

  const { icon, message, theme } = getsStatusDetails();

  let programStatusMessage1 = programStatus;
  let programStatusMessage2 = (
    <UitkText align="center" theme={theme}>
      <span>{l10n.formatText('program.status.message.partnerize', camref)}</span>
    </UitkText>
  );

  if (program.network === 'CJ') {
    programStatusMessage1 = l10n.formatText('program.status.message', programStatus, partnerId);
    programStatusMessage2 = (<></>);
  }

  return (
    <>
      <UitkText align="center" theme={theme}>
        <UitkIcon name={icon} /> <span>{programStatusMessage1}</span>
      </UitkText>{' '}
      {programStatusMessage2}
      {message && program.network === 'CJ' && (
        <UitkSpacing padding={{ blockstart: 'three' }}>
          <UitkText align="center">
            {message}
            {programStatus === 'EXPIRED' && (
              <>
                {' '}
                <UitkSpacing padding={{ blockstart: 'three' }}>
                  <UitkLink inline={true}>
                    <a href={program.signInLink}>
                      <LocalizedText message="partner.sign.in.link" />
                    </a>
                  </UitkLink>
                </UitkSpacing>
              </>
            )}
          </UitkText>
        </UitkSpacing>
      )}
    </>
  );
};
